import { useAuth0 } from "@auth0/auth0-react";
import React, { useState } from "react";
import { AiOutlineDelete } from "react-icons/ai";
import { useCurrentUserAccountsDetailsQuery, useUpdateUserAccountMutation, useEditGroupAccountMutation, useAddAccountToUserMutation, useAddAccountToGroupMutation, useLinkBankAccountToAccountMutation, useDeleteUserAccountMutation, useDeleteGroupAccountMutation, usePowensWebviewCodeLazyQuery, AccountType, CurrentUserAccountsDetailsDocument } from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";
import { extractGroupIdFromComposite } from "../utils/idHelpers";
import { formatAmount } from "../utils/formatAmount";

const Accounts: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth0();
  const { loading, data, error, refetch } = useCurrentUserAccountsDetailsQuery({
    skip: !isAuthenticated,
  });
  const [updateUserAccount, { loading: updating }] = useUpdateUserAccountMutation();
  const [editGroupAccount, { loading: updatingGroup }] = useEditGroupAccountMutation();
  const [addAccountToUser, { loading: addingUser }] = useAddAccountToUserMutation();
  const [addAccountToGroup, { loading: addingGroup }] = useAddAccountToGroupMutation();
  const [linkBankAccountToAccount, { loading: linking }] = useLinkBankAccountToAccountMutation();
  const [deleteUserAccount, { loading: deletingUser }] = useDeleteUserAccountMutation();
  const [deleteGroupAccount, { loading: deletingGroup }] = useDeleteGroupAccountMutation();
  const [getPowensWebviewCode, { loading: loadingWebviewCode }] = usePowensWebviewCodeLazyQuery();

  const openWebview = async () => {
    const manageUrl = new URL("https://webview.powens.com/manage");
    manageUrl.searchParams.set("domain", "jta-money-sandbox.biapi.pro");
    manageUrl.searchParams.set("client_id", "27790729");
    manageUrl.searchParams.set("redirect_uri", "https://react.money.tassin.work/accounts/connect");
    manageUrl.searchParams.set("connector_capabilities", "bank");
    try {
      const result = await getPowensWebviewCode({ fetchPolicy: 'network-only' });
      if (result.data?.powensWebviewCode) {
        manageUrl.searchParams.set("code", result.data.powensWebviewCode);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du code Powens:", err);
    }
    window.open(manageUrl.toString(), "_blank");
  };

  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [linkingAccountId, setLinkingAccountId] = useState<string | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<{ id: string; name: string; groupId?: string } | null>(null);
  const [linkingBankAccountId, setLinkingBankAccountId] = useState<string>('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAccountForm, setNewAccountForm] = useState<{
    owner: string;
    name: string;
    type: AccountType;
    enabled: boolean;
  }>({
    owner: '',
    name: '',
    type: AccountType.Disponibilite,
    enabled: true,
  });
  const [editedAccounts, setEditedAccounts] = useState<Record<string, {
    name: string;
    enabled: boolean;
    type: AccountType;
    bankAccountId: string | null;
    groupId?: string; // groupId si le compte appartient à un groupe
  }>>({});
  const { toast, setToast } = useToast();

  if (loading || isLoading) {
    return <div className="text-center text-gray-500">Chargement...</div>;
  }

  if (!isAuthenticated) {
    return (
      <div className="text-center text-red-500">
        Veuillez vous connecter pour accéder à la section Comptes bancaires
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Comptes bancaires</h1>
        <ErrorMessage 
          error={error}
          onRetry={() => refetch()}
          title="Erreur lors du chargement des comptes"
          className="mb-6"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Comptes bancaires</h1>
        <ErrorMessage 
          onRetry={() => refetch()}
          title="Erreur lors du chargement des comptes - pas de données"
          className="mb-6"
        />
      </div>
    );
  }


  const formatAccountType = (type: string): string => {
    const typeMap: Record<string, string> = {
      DISPONIBILITE: "Disponibilité",
      EPARGNE_BLOQUEE: "Épargne bloquée",
      EPARGNE_NON_BLOQUE: "Épargne non bloquée",
      IMMOBILISATION: "Immobilisation",
      PROVISION: "Provision",
      EMPRUNT: "Emprunt",
    };
    return typeMap[type] || type;
  };

  const formatBankStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      OK: "OK",
      ERROR: "Erreur",
      RENEW_NEEDED: "Renouvellement requis",
    };
    return statusMap[status] || status;
  };

  const getBankStatusColor = (status: string): string => {
    const colorMap: Record<string, string> = {
      OK: "bg-green-100 text-green-800 border-green-200",
      ERROR: "bg-red-100 text-red-800 border-red-200",
      RENEW_NEEDED: "bg-yellow-100 text-yellow-800 border-yellow-200",
    };
    return colorMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getBankAccountName = (bankAccountId: string | null | undefined): string | null => {
    if (!bankAccountId || !data?.banksOfUser) return null;
    
    for (const bank of data.banksOfUser) {
      const bankAccount = bank.accounts.find((acc) => acc.id === bankAccountId);
      if (bankAccount) {
        return `${bank.name} - ${bankAccount.name}`;
      }
    }
    return null;
  };

  const getAllBankAccounts = () => {
    if (!data?.banksOfUser) return [];
    const allAccounts: Array<{ id: string; name: string; bankName: string }> = [];
    data.banksOfUser.forEach((bank) => {
      bank.accounts.forEach((account) => {
        allAccounts.push({
          id: account.id,
          name: account.name,
          bankName: bank.name,
        });
      });
    });
    return allAccounts;
  };

  const handleEdit = (account: {
    id: string;
    name: string;
    type: AccountType;
    enabled?: boolean | null;
    bankAccount?: { id: string } | null;
  }, groupId?: string) => {
    setEditingAccountId(account.id);
    setEditedAccounts({
      ...editedAccounts,
      [account.id]: {
        name: account.name,
        enabled: account.enabled ?? true,
        type: account.type,
        bankAccountId: account.bankAccount?.id || null,
        groupId: groupId, // Stocker le groupId si le compte appartient à un groupe
      },
    });
  };

  const handleCancel = () => {
    setEditingAccountId(null);
  };

  const handleSave = async (accountId: string) => {
    if (!editedAccounts[accountId]) return;

    const accountData = editedAccounts[accountId];
    const isGroupAccount = accountData.groupId && accountData.groupId !== "";

    try {
      if (isGroupAccount) {
        // Utiliser la mutation pour éditer un compte de groupe
        if (!accountData.groupId) {
          setToast({ message: "Impossible de déterminer le groupe du compte", type: "error" });
          return;
        }
        // Extraire l'ID réel du groupe si c'est un ID composite
        const realGroupId = extractGroupIdFromComposite(accountData.groupId);
        await editGroupAccount({
          variables: {
            groupId: realGroupId,
            account: {
              id: accountId,
              name: accountData.name,
              enabled: accountData.enabled,
              type: accountData.type,
              bankAccountId: accountData.bankAccountId ?? null,
            },
          },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      } else {
        // Utiliser la mutation pour éditer un compte utilisateur
        if (!data?.currentUser?.id) {
          setToast({ message: "Impossible de déterminer l'utilisateur", type: "error" });
          return;
        }
        await updateUserAccount({
          variables: {
            userId: data.currentUser.id,
            account: {
              id: accountId,
              name: accountData.name,
              enabled: accountData.enabled,
              type: accountData.type,
              bankAccountId: accountData.bankAccountId ?? null,
            },
          },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      }
      setEditingAccountId(null);
      setToast({ message: "Compte mis à jour avec succès", type: "success" });
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour du compte:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la mise à jour du compte";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  const handleDelete = async () => {
    if (!accountToDelete) return;
    try {
      if (accountToDelete.groupId) {
        await deleteGroupAccount({
          variables: { groupId: accountToDelete.groupId, accountId: accountToDelete.id },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      } else {
        if (!data?.currentUser?.id) return;
        await deleteUserAccount({
          variables: { userId: data.currentUser.id, accountId: accountToDelete.id },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      }
      setAccountToDelete(null);
      setToast({ message: "Compte supprimé avec succès", type: "success" });
    } catch (err: any) {
      const errorMessage = err?.message || "Une erreur est survenue lors de la suppression";
      setToast({ message: errorMessage, type: "error" });
      setAccountToDelete(null);
    }
  };

  const handleLinkBankAccount = async (accountId: string) => {
    if (!linkingBankAccountId) {
      setToast({ message: "Veuillez sélectionner un compte bancaire", type: "error" });
      return;
    }
    try {
      await linkBankAccountToAccount({
        variables: {
          accountId,
          budgetInsideBankAccount: linkingBankAccountId,
        },
        refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
      });
      setLinkingAccountId(null);
      setLinkingBankAccountId('');
      setToast({ message: "Compte lié avec succès", type: "success" });
    } catch (err: any) {
      const errorMessage = err?.message || "Une erreur est survenue lors du lien";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  const handleFieldChange = (accountId: string, field: string, value: any) => {
    setEditedAccounts({
      ...editedAccounts,
      [accountId]: {
        ...editedAccounts[accountId],
        [field]: value,
      },
    });
  };

  const handleAddAccount = async () => {
    if (!newAccountForm.name.trim()) {
      setToast({ message: "Le nom du compte est requis", type: "error" });
      return;
    }
    if (!newAccountForm.owner) {
      setToast({ message: "Veuillez sélectionner un propriétaire", type: "error" });
      return;
    }

    try {
      if (newAccountForm.owner === 'user') {
        if (!data?.currentUser?.id) {
          setToast({ message: "Impossible de déterminer l'utilisateur", type: "error" });
          return;
        }
        await addAccountToUser({
          variables: {
            userId: data.currentUser.id,
            name: newAccountForm.name.trim(),
            type: newAccountForm.type,
            enabled: newAccountForm.enabled,
          },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      } else {
        const realGroupId = extractGroupIdFromComposite(newAccountForm.owner);
        await addAccountToGroup({
          variables: {
            groupId: realGroupId,
            name: newAccountForm.name.trim(),
            type: newAccountForm.type,
            enabled: newAccountForm.enabled,
          },
          refetchQueries: [{ query: CurrentUserAccountsDetailsDocument }],
        });
      }
      setShowAddForm(false);
      setNewAccountForm({ owner: '', name: '', type: AccountType.Disponibilite, enabled: true });
      setToast({ message: "Compte créé avec succès", type: "success" });
    } catch (err: any) {
      const errorMessage = err?.message || "Une erreur est survenue lors de la création du compte";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      {accountToDelete && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setAccountToDelete(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-md p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Supprimer le compte</h3>
              <p className="text-sm text-gray-600 mb-1">
                Voulez-vous supprimer définitivement le compte <span className="font-medium">«&nbsp;{accountToDelete.name}&nbsp;»</span> ?
              </p>
              <p className="text-sm text-red-600 mb-6">Cette action est irréversible.</p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setAccountToDelete(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deletingUser || deletingGroup}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-hidden focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {(deletingUser || deletingGroup) ? "Suppression..." : "Supprimer"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Comptes bancaires</h1>

      {/* Section des banques */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Banques connectées</h2>
          <button
            onClick={openWebview}
            disabled={loadingWebviewCode}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-60"
          >
            {loadingWebviewCode ? "Chargement..." : "Gérer vos connexions"}
          </button>
        </div>
        {data?.banksOfUser && data.banksOfUser.length > 0 ? (
          <div className="space-y-3">
            {data.banksOfUser.map((bank) => (
              <div
                key={bank.id}
                className="bg-blue-50 rounded-lg shadow-md p-4 border border-blue-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {bank.name}
                      </h3>
                      {bank.status && (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getBankStatusColor(bank.status)}`}
                          title={`Statut de la connexion : ${formatBankStatus(bank.status)}`}
                        >
                          {formatBankStatus(bank.status)}
                        </span>
                      )}
                    </div>
                    {(bank.status === "RENEW_NEEDED" || bank.status === "ERROR") && (
                      <div className="mt-2 flex items-center gap-3">
                        <p className="text-sm text-amber-700 italic">
                          {bank.status === "RENEW_NEEDED"
                            ? "⚠️ La connexion nécessite un renouvellement."
                            : "❌ Une erreur est survenue avec cette connexion."}
                        </p>
                        <button
                          onClick={openWebview}
                          disabled={loadingWebviewCode}
                          className="px-3 py-1 text-sm font-medium text-amber-700 bg-amber-100 rounded-md hover:bg-amber-200 focus:outline-hidden focus:ring-2 focus:ring-amber-500 transition-colors whitespace-nowrap disabled:opacity-60"
                        >
                          {loadingWebviewCode ? "Chargement..." : "Ré-authentifier"}
                        </button>
                      </div>
                    )}
                    {bank.accounts && bank.accounts.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-600 mb-1">Comptes bancaires :</p>
                        <div className="flex flex-wrap gap-2">
                          {bank.accounts.map((account) => (
                            <span
                              key={account.id}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {account.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
            <p className="text-gray-500 italic text-center">
              Aucune banque connectée pour le moment.
            </p>
          </div>
        )}
      </div>

      {/* Section des comptes */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Comptes</h2>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            if (!showAddForm) {
              const firstGroup = data?.groups?.[0];
              setNewAccountForm({
                owner: firstGroup?.id ?? 'user',
                name: '',
                type: AccountType.Disponibilite,
                enabled: true,
              });
            }
          }}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
        >
          {showAddForm ? "Annuler" : "+ Nouveau compte"}
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-blue-200">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Nouveau compte</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Propriétaire</label>
              <select
                value={newAccountForm.owner}
                onChange={(e) => setNewAccountForm({ ...newAccountForm, owner: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                {data?.currentUser && (
                  <option value="user">{data.currentUser.firstname} (moi)</option>
                )}
                {data?.groups?.map((group) => (
                  <option key={group.id} value={group.id}>{group.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
              <input
                type="text"
                value={newAccountForm.name}
                onChange={(e) => setNewAccountForm({ ...newAccountForm, name: e.target.value })}
                placeholder="Ex: Livret A, Compte courant..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={newAccountForm.type}
                onChange={(e) => setNewAccountForm({ ...newAccountForm, type: e.target.value as AccountType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              >
                <option value="DISPONIBILITE">Disponibilité</option>
                <option value="EPARGNE_BLOQUEE">Épargne bloquée</option>
                <option value="EPARGNE_NON_BLOQUE">Épargne non bloquée</option>
                <option value="IMMOBILISATION">Immobilisation</option>
                <option value="PROVISION">Provision</option>
                <option value="EMPRUNT">Emprunt</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="new-account-enabled"
                checked={newAccountForm.enabled}
                onChange={(e) => setNewAccountForm({ ...newAccountForm, enabled: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
              />
              <label htmlFor="new-account-enabled" className="ml-2 block text-sm text-gray-700">Activé</label>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleAddAccount}
                disabled={addingUser || addingGroup}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {(addingUser || addingGroup) ? "Création..." : "Créer le compte"}
              </button>
            </div>
          </div>
        </div>
      )}
      {(() => {
        // Type pour les comptes avec groupe
        type AccountWithGroup = {
          account: {
            id: string;
            amount: number;
            name: string;
            type: any;
            enabled?: boolean | null;
            hasTransactions: boolean;
            bankAccount?: { id: string } | null;
          };
          groupName: string;
          groupId?: string;
        };

        // Collecter les comptes : d'abord currentUser, puis les foyers/groupes
        const allAccountsWithGroup: AccountWithGroup[] = [];

        // Comptes du currentUser
        if (data?.currentUser) {
          data.currentUser.accounts?.forEach((account) => {
            allAccountsWithGroup.push({
              account: {
                id: account.id,
                amount: account.amount,
                name: account.name,
                type: account.type,
                enabled: account.enabled,
                hasTransactions: account.hasTransactions,
                bankAccount: account.bankAccount || null,
              },
              groupName: data.currentUser!.firstname,
              groupId: undefined,
            });
          });
        }

        // Comptes des foyers/groupes
        if (data?.groups) {
          data.groups.forEach((group) => {
            group.accounts?.forEach((account) => {
              allAccountsWithGroup.push({
                account: {
                  id: account.id,
                  amount: account.amount,
                  name: account.name,
                  type: account.type,
                  enabled: account.enabled,
                  hasTransactions: account.hasTransactions,
                  bankAccount: account.bankAccount || null,
                },
                groupName: group.name,
                groupId: group.id,
              });
            });
          });
        }
        
        if (allAccountsWithGroup.length === 0) {
          return (
            <div className="bg-white rounded-lg shadow-md p-6">
              <p className="text-gray-500 italic text-center">
                Aucun compte disponible pour le moment.
              </p>
            </div>
          );
        }
        
        return (
          <div className="space-y-3">
            {allAccountsWithGroup
              .sort((a, b) => {
                // Trier d'abord par groupe (ordre alphabétique)
                const groupCompare = a.groupName.localeCompare(b.groupName, "fr", { sensitivity: "base" });
                if (groupCompare !== 0) return groupCompare;
                
                // Puis par statut (actifs en premier)
                if (a.account.enabled !== b.account.enabled) {
                  return a.account.enabled ? -1 : 1;
                }
                // Puis par ordre alphabétique du nom
                return a.account.name.localeCompare(b.account.name, "fr", { sensitivity: "base" });
              })
              .map(({ account, groupName, groupId }) => {
            const isEditing = editingAccountId === account.id;
            const editedAccount = editedAccounts[account.id];
            const allBankAccounts = getAllBankAccounts();

            return (
              <div
                key={account.id}
                className="bg-white rounded-lg shadow-md p-4 hover:shadow-md transition-shadow"
              >
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom
                      </label>
                      <input
                        type="text"
                        value={editedAccount?.name || ""}
                        onChange={(e) => handleFieldChange(account.id, "name", e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        value={editedAccount?.type || ""}
                        onChange={(e) => handleFieldChange(account.id, "type", e.target.value as AccountType)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="DISPONIBILITE">Disponibilité</option>
                        <option value="EPARGNE_BLOQUEE">Épargne bloquée</option>
                        <option value="EPARGNE_NON_BLOQUE">Épargne non bloquée</option>
                        <option value="IMMOBILISATION">Immobilisation</option>
                        <option value="PROVISION">Provision</option>
                        <option value="EMPRUNT">Emprunt</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Compte bancaire
                      </label>
                      <select
                        value={editedAccount?.bankAccountId || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFieldChange(account.id, "bankAccountId", value === "" ? null : value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Aucun</option>
                        {allBankAccounts.map((bankAcc) => (
                          <option key={bankAcc.id} value={bankAcc.id}>
                            {bankAcc.bankName} - {bankAcc.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`enabled-${account.id}`}
                        checked={editedAccount?.enabled ?? false}
                        onChange={(e) => handleFieldChange(account.id, "enabled", e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded-sm"
                      />
                      <label htmlFor={`enabled-${account.id}`} className="ml-2 block text-sm text-gray-700">
                        Activé
                      </label>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => handleSave(account.id)}
                        disabled={updating || updatingGroup}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {(updating || updatingGroup) ? "Enregistrement..." : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {account.name}
                            </h3>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700">
                              {groupName}
                            </span>
                          </div>
                          <div className="mt-1 space-y-1">
                            <p className="text-sm text-gray-500">ID: {account.id}</p>
                            {account.type && (
                              <p className="text-sm text-gray-600">
                                Type: <span className="font-medium">{formatAccountType(account.type)}</span>
                              </p>
                            )}
                            {account.bankAccount?.id && (
                              <p className="text-sm text-gray-600">
                                Compte bancaire: <span className="font-medium">{getBankAccountName(account.bankAccount.id) || account.bankAccount.id}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">
                            {formatAmount(account.amount)}
                          </p>
                        </div>
                      </div>
                      <div className="ml-4 flex items-center space-x-2">
                        {account.enabled ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Activé
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Désactivé
                          </span>
                        )}
                        {groupId && !account.bankAccount?.id && getAllBankAccounts().length > 0 && (
                          <button
                            onClick={() => {
                              setLinkingAccountId(account.id);
                              setLinkingBankAccountId(getAllBankAccounts()[0]?.id ?? '');
                            }}
                            className="px-3 py-1 text-sm font-medium text-purple-600 bg-purple-50 rounded-md hover:bg-purple-100 focus:outline-hidden focus:ring-2 focus:ring-purple-500"
                          >
                            Lier à une banque
                          </button>
                        )}
                        {!account.hasTransactions && (
                          <button
                            onClick={() => setAccountToDelete({ id: account.id, name: account.name, groupId })}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                            title="Supprimer ce compte"
                          >
                            <AiOutlineDelete size={18} />
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(account, groupId)}
                          className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                        >
                          Modifier
                        </button>
                      </div>
                    </div>
                    {linkingAccountId === account.id && (
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3">
                        <select
                          value={linkingBankAccountId}
                          onChange={(e) => setLinkingBankAccountId(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-purple-500 text-sm"
                        >
                          {getAllBankAccounts().map((bankAcc) => (
                            <option key={bankAcc.id} value={bankAcc.id}>
                              {bankAcc.bankName} - {bankAcc.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleLinkBankAccount(account.id)}
                          disabled={linking}
                          className="px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 focus:outline-hidden focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                          {linking ? "Liaison..." : "Confirmer"}
                        </button>
                        <button
                          onClick={() => { setLinkingAccountId(null); setLinkingBankAccountId(''); }}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500"
                        >
                          Annuler
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        );
      })()}
      </div>
    </>
  );
};

export default Accounts;

